import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Amplify, API, graphqlOperation } from "aws-amplify";
import { updateSharedApp, createSharedApp } from "../../graphql/mutations";
import { getRequests, getSharedApp } from "../../graphql/queries";
import { appsByClientId } from "../../graphql/custom-queries";
import {
  Heading,
  Button as LumosButton,
  useNotify,
  Card as LumosCard,
  Collapsible
 } from "@loanpal/lumos__core";
import { Box } from "theme-ui";
import Loading from "../../utils/loading";
import {
  normalizeRequestStates,
  serializeSharedAppStates,
} from "../../utils/shared-app-states";
import {
  DEFAULT_PAYMENTS_SHARE_METHODS,
  normalizePaymentsShareMethods,
} from "../request/payments-tab";
import NavBar from "../../shared/nav-bar";
import { formFields, components } from "../../utils/authentication";
import { useNavigate } from "react-router-dom";
import { Categories } from "../../utils/categories";
import { Status } from "../../utils/status";
import {
  getToken as getTokenApi,
  getCategories as getCategoriesApi,
  getOffersByCategory as getOffersByCategoryApi,
  getOffersForSharedApp,
  getOfferRowId,
  normalizeOffersHttpBody
} from "../../utils/api";
import {
  normalizeUrlSlug,
  sharedAppUrlPathClient,
} from "../../utils/url-slug";

import {
  // withAuthenticator,
  Authenticator,
  Button,
  TextField,
  SwitchField,
  Divider,
  SearchField,
  View,
  Flex,
  FieldGroupIconButton,
  Alert,
  SelectField,
  Text
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import awsExports from "../../aws-exports";
Amplify.configure(awsExports);

/** Standard UUID string (8-4-4-4-12 hex). Used when integratorId may double as explicit SharedApp `id` (non-SRS flows). */
const UUID_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isAcceptableUuidForPartnerId(value) {
  if (value == null || typeof value !== "string") return false;
  return UUID_ID_RE.test(value.trim());
}

/** Roof Hub SRS flow: integrator id must match exactly; requires organizationId on form. */
const SRS_INTEGRATOR = "SRSIntegrator";

/**
 * Roof Hub / SRS create path: parentOrganizationClientId is SRSIntegrator and the
 * real Roof Hub integrator UUID lives in integratorId (not the literal "SRSIntegrator").
 * When organizationId is set, SharedApp primary key must be organizationId so apply
 * routes like /rh-:integrator/:id resolve via getSharedApp({ id }).
 */
function shouldUseOrganizationIdAsSharedAppId(formState) {
  const parent = (formState.parentOrganizationClientId || "").trim();
  const integrator = (formState.integratorId || "").trim();
  const org = (formState.organizationId || "").trim();
  return parent === SRS_INTEGRATOR && !!integrator && !!org;
}

function buildSrsGoodleapplusFormUrl(formState) {
  const parentOrganizationClientId = (formState.parentOrganizationClientId || "").trim();
  const integratorId = (formState.integratorId || "").trim();
  const organizationId = (formState.organizationId || "").trim();
  if (parentOrganizationClientId !== SRS_INTEGRATOR || !integratorId) return null;
  const host = window.location.host;
  return `https://${host}/goodleapplus/${encodeURIComponent(organizationId)}`;
}

const initialState = {
  id: "",
  clientId: "",
  status: "",
  partnerName: "",
  integratorId: "",
  channel: "",
  states: null,
  category: "",
  loanAmount: 0.0,
  paymentAmount: 0.0,
  calculatorType: "LA",
  redirectUrl: "",
  sendEmail: false,
  displayIframe: true,
  referenceId: "quickLink",
  formUrl: "",
  submittingUserFname: "",
  submittingUserLname: "",
  submittingUserEmail: "",
  submittingUserPhone: "",
  bundleClientId: "",
  singleOfferLinks: false,
  useConnectedOrgs: false,
  parentOrganizationClientId: "",
  organizationId: "",
  urlSlug: "",
  /** Comma-separated offer ids (matches Request form / SharedApp `offers` AWSJSON). */
  offer: "",
  apiKeys: false,
  paymentsShareMethods: { ...DEFAULT_PAYMENTS_SHARE_METHODS },
};

/** SharedApp.offers (AWSJSON) → comma-separated ids for the form. */
function sharedAppOffersToOfferCsv(offers) {
  if (offers == null || offers === "") return "";
  let parsed = offers;
  if (typeof offers === "string") {
    try {
      parsed = JSON.parse(offers);
    } catch {
      return "";
    }
  }
  if (!Array.isArray(parsed)) return "";
  return parsed
    .map((o) => {
      if (o == null) return "";
      if (typeof o === "string" || typeof o === "number") return String(o).trim();
      if (typeof o === "object") {
        const id = o.id ?? o.offerId ?? o.value ?? o.uuid;
        if (id != null && typeof id !== "object") return String(id).trim();
      }
      return "";
    })
    .filter(Boolean)
    .join(",");
}

function convertOffersToAWSJSON(offerCsv) {
  if (!offerCsv || typeof offerCsv !== "string" || !offerCsv.trim()) {
    return JSON.stringify([]);
  }
  const ids = offerCsv.split(",").map((s) => s.trim()).filter(Boolean);
  return JSON.stringify(ids.map((id) => ({ id })));
}

/** Maps form category enum to API category name (same as Request view). */
function categoryNameForOffersApi(cat) {
  if (cat === "HOME_IMPROVEMENT") return "HOME IMPROVEMENT";
  if (cat === "SOLAR") return "SOLAR";
  if (cat === "BATTERY") return "BATTERY";
  if (cat === "ROOF") return "ROOFING / WINDOWS / DOORS / SIDING / GEOTHERMAL / RESTORATION";
  if (cat === "REMODELING") return "REMODELING";
  return cat || "";
}

const editOffersEnv = "prod";

/** Display label for an offer row from GET /offers (aligned with apply flow). */
function catalogOfferDisplayName(o) {
  if (!o) return "Unknown offer";
  const code = o.productCode || "";
  const primary = o.name || o.offerName || "";
  const product = o.productName || "";
  if (typeof code === "string" && code.includes("DEFERRED") && product && primary) {
    return `${product} - ${primary}`;
  }
  if (primary && product) return `${primary} - ${product}`;
  return primary || product || "Unknown offer";
}

const Edit = () => {
  const [formState, setFormState] = useState(initialState);
  const [spinner, setSpinner] = useState(false);
  const [catalogOffers, setCatalogOffers] = useState([]);
  const [catalogOffersStatus, setCatalogOffersStatus] = useState("idle");
  const { id, action } = useParams();
  const [placeholderUrl] = useState(
    `https://${window.location.host}/{clientId}/{id}`
  );
  let navigate = useNavigate();
  const { addSnack } = useNotify();

  const catalogById = useMemo(() => {
    const m = new Map();
    catalogOffers.forEach((o) => {
      const rid = getOfferRowId(o);
      if (rid) m.set(rid, o);
    });
    return m;
  }, [catalogOffers]);

  useEffect(() => {
    if (id !== 'new' && action === undefined) {
      fetchSharedApp(id);
    } else if (id === 'new') {
      clearForm();
    } else if (action === 'request' && id !== "") {
      fetchRequest(id)
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      const selectedOfferIds = (formState.offer || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (selectedOfferIds.length === 0) {
        setCatalogOffers([]);
        setCatalogOffersStatus("idle");
        return;
      }
      const clientId = (formState.clientId || "").trim();
      if (!clientId) {
        setCatalogOffers([]);
        setCatalogOffersStatus("idle");
        return;
      }
      if (!formState.apiKeys) {
        const hasConnectedOrgCredentials =
          formState.useConnectedOrgs &&
          formState.parentOrganizationClientId &&
          formState.organizationId;
        if (!hasConnectedOrgCredentials) {
          setCatalogOffers([]);
          setCatalogOffersStatus("no_keys");
          return;
        }
      }
      setCatalogOffersStatus("loading");
      try {
        let resolvedToken;
        if (
          formState.useConnectedOrgs &&
          formState.parentOrganizationClientId &&
          formState.organizationId
        ) {
          const t = await getTokenApi(
            formState.parentOrganizationClientId,
            formState.organizationId
          );
          resolvedToken = t?.token;
          if (!resolvedToken) {
            if (cancelled) return;
            setCatalogOffers([]);
            setCatalogOffersStatus("error");
            return;
          }
        }

        const sharedOfferParams = {
          clientId: formState.clientId,
          useConnectedOrgs: formState.useConnectedOrgs,
          parentOrganizationClientId: formState.parentOrganizationClientId,
          organizationId: formState.organizationId,
          token: resolvedToken ?? null
        };

        let rows = [];
        const category = (formState.category || "").trim();
        if (category) {
          const categoriesList = await getCategoriesApi(
            formState.clientId,
            resolvedToken
          );
          if (Array.isArray(categoriesList) && categoriesList.length > 0) {
            const apiName = categoryNameForOffersApi(formState.category);
            const cat = categoriesList.find((el) => el.name === apiName);
            if (cat) {
              const byCatRaw = await getOffersByCategoryApi(
                formState.clientId,
                cat.id,
                "",
                editOffersEnv,
                resolvedToken,
                (formState.channel || "").trim() || undefined
              );
              if (byCatRaw !== false && byCatRaw != null) {
                rows = normalizeOffersHttpBody(byCatRaw);
              }
            }
          }
        }

        if (rows.length === 0) {
          const allRes = await getOffersForSharedApp({
            ...sharedOfferParams,
            allOffers: true
          });
          rows = Array.isArray(allRes?.data) ? allRes.data : [];
        }
        const channel = (formState.channel || "").trim();
        if (rows.length === 0 && channel) {
          const chRes = await getOffersForSharedApp({
            ...sharedOfferParams,
            channel: formState.channel
          });
          if (chRes && Array.isArray(chRes.data)) {
            rows = chRes.data;
          }
        }
        if (cancelled) return;
        setCatalogOffers(rows);
        setCatalogOffersStatus("ready");
      } catch (err) {
        if (cancelled) return;
        console.log("error loading catalog offers for edit", err);
        setCatalogOffers([]);
        setCatalogOffersStatus("error");
      }
    };
    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [
    formState.offer,
    formState.clientId,
    formState.channel,
    formState.category,
    formState.apiKeys,
    formState.useConnectedOrgs,
    formState.parentOrganizationClientId,
    formState.organizationId
  ]);

  function setInput(key, value) {
    if (key === "integratorId") {
      setFormState({ ...formState, [key]: value, referenceId: "integratorId" });
    } else {
      setFormState({ ...formState, [key]: value });
    }
  }

  const clearForm = () => {
    setFormState(initialState);
  }

  /**
   * Load a partner by SharedApp primary key (`id`, UUID, SRS org id, etc.) or by `clientId`
   * (legacy URLs / search). Canonical URL is `/edit/:id`.
   */
  async function fetchSharedApp(param) {
    const raw = typeof param === "string" ? param.trim() : String(param).trim();
    if (!raw) return;
    try {
      const byIdRes = await API.graphql(graphqlOperation(getSharedApp, { id: raw }));
      let app = byIdRes.data?.getSharedApp ?? null;

      if (!app) {
        const appData = await API.graphql(
          graphqlOperation(appsByClientId, { clientId: raw })
        );
        const apps = appData.data.appsByClientId.items;
        if (apps && apps.length > 0) {
          if (apps.length > 1) {
            addSnack({
              variant: "warning",
              message: `Several partners share client id “${raw}”; opened the first match. Use the partner id from the Partners list to open a specific row.`,
            });
          }
          app = apps[0];
        }
      }

      if (app) {
        await loadSharedApp(app);
        if (app.id && app.id !== id) {
          navigate(`../edit/${app.id}`, { replace: true });
        }
      } else {
        addSnack({ variant: "danger", message: "Partner not found." });
      }
    } catch (err) {
      console.log("error fetching Shared App", err);
      addSnack({ variant: "danger", message: "Partner not found." });
    }
  }

  async function checkSharedAppExists(req) {
    if (!req.clientId && req.clientId === "") return;
    try {
      const appData = await API.graphql(
        graphqlOperation(appsByClientId, { clientId: req.clientId })
      );
      const apps = appData.data.appsByClientId.items;
      console.log("APP", apps);
      if (apps && apps.length > 0) {
        const reqIntegratorId = (req.integratorId || "").trim();
        let match = null;
        if (reqIntegratorId) {
          match = apps.find(
            (a) => (a.integratorId || "").trim() === reqIntegratorId
          );
        } else {
          match = apps[0];
        }
        if (match) {
          console.log(match);
          loadSharedApp(match);
        } else {
          loadRequest(req);
        }
      } else {
        loadRequest(req);
      }
    } catch (err) {
      console.log("error fetching Shared App", err);
      return false;
    }
  }

  async function fetchRequest(id) {
    if (!id && id === "") return;
    try {
      const reqData = await API.graphql(
        graphqlOperation(getRequests, { id: id })
      );
      const req = reqData.data.getRequests;
      if (req) {
        checkSharedAppExists(req);
      }
    } catch (err) {
      console.log("error fetching Request", err);
    }
  }

  const loadRequest  = async (app) => {
    let keysExists = app.apiKeys;
    if (!app.apiKeys) {
      setSpinner(true);
      try {
        if (app.useConnectedOrgs && app.parentOrganizationClientId && app.organizationId) {
          keysExists = await checkElevatedToken(app.clientId, app.parentOrganizationClientId, app.organizationId);
        } else {
          keysExists = await checkApiKeys(app.clientId);
        }
      } catch (err) {
        console.log("Error checking API keys", err);
        addSnack({ variant: 'warning', message: 'Error checking API keys' });
      } finally {
        setSpinner(false);
      }
    }
    const appState = {
      id: "",
      clientId: app.clientId || "",
      status: app.status || "",
      partnerName: app.accountName || "",
      integratorId: app.integratorId || "",
      redirectUrl: app.redirectUrl || "",
      channel: app.channel || "",
      states: normalizeRequestStates(
        app.states ? JSON.parse(app.states) : null
      ),
      loanAmount: app.loanAmount || 0.0,
      paymentAmount: app.paymentAmount || 0.0,
      calculatorType: app.calculatorType || "LA",
      category: app.category || "",
      sendEmail: app.contactEmail || false,
      displayIframe: true,
      referenceId: app.referenceId || "quickLink",
      formUrl: "",
      submittingUserFname: app.submittingUser?.firstName || "",
      submittingUserLname: app.submittingUser?.lastName || "",
      submittingUserEmail: app.submittingUser?.email || "",
      submittingUserPhone: app.submittingUser?.phone || "",
      apiKeys: keysExists,
      bundleClientId: app.bundleClientId || "",
      singleOfferLinks: app.singleOfferLinks || false,
      useConnectedOrgs: app.useConnectedOrgs || false,
      parentOrganizationClientId: app.parentOrganizationClientId || "",
      organizationId: app.organizationId || "",
      urlSlug: app.urlSlug || "",
      offer: app.offer || "",
      paymentsShareMethods: normalizePaymentsShareMethods(app.payments),
    };
    console.log("In loadRequest appState", appState);
    setFormState(appState);
  };

  const loadSharedApp = async (app) => {
    let keysExists = app.apiKeys;
    if (!app.apiKeys) {
      setSpinner(true);
      try {
        if (app.useConnectedOrgs && app.parentOrganizationClientId && app.organizationId) {
          keysExists = await checkElevatedToken(app.clientId, app.parentOrganizationClientId, app.organizationId);
        } else {
          keysExists = await checkApiKeys(app.clientId);
        }
      } catch (err) {
        console.log("Error checking API keys", err);
        addSnack({ variant: 'warning', message: 'Error checking API keys' });
      } finally {
        setSpinner(false);
      }
    }
    const appState = {
      id: app.id || "",
      clientId: app.clientId || "",
      status: app.status || "",
      partnerName: app.partnerName || "",
      integratorId: app.integratorId || "",
      redirectUrl: app.redirectUrl || "",
      channel: app.channel || "",
      states: normalizeRequestStates(
        app.states ? JSON.parse(app.states) : null
      ),
      loanAmount: app.loanAmount || 0.0,
      paymentAmount: app.paymentAmount || 0.0,
      calculatorType: app.calculatorType || "LA",
      category: app.category || "",
      sendEmail: app.sendEmail || false,
      displayIframe: app.displayIframe || true,
      referenceId: app.referenceId || "quickLink",
      formUrl: app.formUrl || "",
      submittingUserFname: app.submittingUser?.firstName || "",
      submittingUserLname: app.submittingUser?.lastName || "",
      submittingUserEmail: app.submittingUser?.email || "",
      submittingUserPhone: app.submittingUser?.phone || "",
      apiKeys: keysExists,
      bundleClientId: app.bundleClientId || "",
      singleOfferLinks: app.singleOfferLinks || false,
      useConnectedOrgs: app.useConnectedOrgs || false,
      parentOrganizationClientId: app.parentOrganizationClientId || "",
      organizationId: app.organizationId || "",
      urlSlug: app.urlSlug || "",
      offer: sharedAppOffersToOfferCsv(app.offers),
      paymentsShareMethods: normalizePaymentsShareMethods(app.payments),
    };
    setFormState(appState);
  };

  const checkElevatedToken = async (clientId, parentOrganizationClientId, organizationId) => {
    try {
      const elevatedToken = await getTokenApi(parentOrganizationClientId, organizationId);
      if (elevatedToken) {
        console.log("Elevated token received", elevatedToken);
        const keysExists = await checkApiKeys(clientId, elevatedToken.token);
        if (keysExists) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (err) {
      console.log("error checking Elevated Token", err);
      return false;
    }
  }

  const checkApiKeys = async (clientId, token) => {
    try {
      const result = await getCategoriesApi(clientId, token);
      if (result.length > 0) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.log("error checking API keys");
      return false;
    }
  };

  const buildApp = (app, update) => {
    if (update)
      return {
        id: app.id ? app.id : "",
        clientId: app.clientId,
        status: app.status,
        partnerName: app.partnerName,
        integratorId: app.integratorId,
        redirectUrl: app.redirectUrl,
        channel: app.channel,
        category: app.category,
        loanAmount: app.loanAmount,
        paymentAmount: app.paymentAmount,
        calculatorType: app.calculatorType,
        states: serializeSharedAppStates(app.states),
        offers: convertOffersToAWSJSON(app.offer),
        submittingUser: {
          firstName: app.submittingUserFname,
          lastName: app.submittingUserLname,
          email: app.submittingUserEmail,
          phone: app.submittingUserPhone,
        },
        sendEmail: app.sendEmail,
        displayIframe: app.displayIframe,
        referenceId: app.referenceId,
        formUrl: app.formUrl,
        bundleClientId: app.bundleClientId,
        singleOfferLinks: app.singleOfferLinks,
        useConnectedOrgs: app.useConnectedOrgs,
        parentOrganizationClientId: app.parentOrganizationClientId,
        organizationId: app.organizationId,
        urlSlug: normalizeUrlSlug(app.urlSlug) || null,
        payments: {
          shareMethods: normalizePaymentsShareMethods({
            shareMethods: app.paymentsShareMethods,
          }),
        },
      };
    else
      return {
        clientId: app.clientId,
        status: app.status,
        partnerName: app.partnerName,
        integratorId: app.integratorId,
        redirectUrl: app.redirectUrl,
        channel: app.channel,
        category: app.category,
        loanAmount: app.loanAmount,
        paymentAmount: app.paymentAmount,
        calculatorType: app.calculatorType,
        states: serializeSharedAppStates(app.states),
        offers: convertOffersToAWSJSON(app.offer),
        submittingUser: {
          firstName: app.submittingUserFname,
          lastName: app.submittingUserLname,
          email: app.submittingUserEmail,
          phone: app.submittingUserPhone,
        },
        sendEmail: app.sendEmail,
        displayIframe: app.displayIframe,
        referenceId: app.referenceId,
        formUrl: app.formUrl,
        bundleClientId: app.bundleClientId,
        singleOfferLinks: app.singleOfferLinks,
        useConnectedOrgs: app.useConnectedOrgs,
        parentOrganizationClientId: app.parentOrganizationClientId,
        organizationId: app.organizationId,
        urlSlug: normalizeUrlSlug(app.urlSlug) || null,
        payments: {
          shareMethods: normalizePaymentsShareMethods({
            shareMethods: app.paymentsShareMethods,
          }),
        },
      };
  };

  async function addSharedApp() {
    try {
      setSpinner(true);
      if (
        !formState.clientId ||
        !formState.channel ||
        !formState.submittingUserEmail ||
        !formState.submittingUserFname ||
        !formState.submittingUserLname
      )
        return;
      if (!formState.sendEmail && !formState.displayIframe) {
        return;
      }
      const srsOnlyMissingOrg =
        (formState.integratorId || "").trim() === SRS_INTEGRATOR &&
        !(formState.organizationId || "").trim();
      if (srsOnlyMissingOrg) {
        addSnack({
          variant: "danger",
          message:
            "Organization ID is required when integrator is SRSIntegrator (Roof Hub link).",
        });
        setSpinner(false);
        return;
      }
      if (formState.id !== "") {
        const srsFormUrl = buildSrsGoodleapplusFormUrl(formState);
        if (srsFormUrl) {
          formState.formUrl = srsFormUrl;
        } else if (formState.integratorId && formState.integratorId !== "" && formState.calculatorType === 'LA') {
          formState.formUrl = `https://${window.location.host}/${formState.integratorId}/${sharedAppUrlPathClient(formState)}/la/${formState.id}`;
        } else if (formState.integratorId && formState.integratorId !== "" && formState.calculatorType === 'PA') {
          formState.formUrl = `https://${window.location.host}/${formState.integratorId}/${sharedAppUrlPathClient(formState)}/${formState.id}`;
        }
        const app = buildApp(formState, formState.id !== "");
        await API.graphql(graphqlOperation(updateSharedApp, { input: app }));
         addSnack({ variant: 'success', message: 'Shared App saved successfully!' });
      } else {
        let createInput = { ...buildApp(formState, false) };
        const integratorTrim = (formState.integratorId || "").trim();
        const organizationTrim = (formState.organizationId || "").trim();
        const srsUseOrgAsId = shouldUseOrganizationIdAsSharedAppId(formState);
        /**
         * SharedApp.id on create (unchanged defaults):
         * - No integratorId, or non-UUID integratorId, or parentOrganizationClientId !== SRSIntegrator
         *   → neither branch below runs → no `id` on input → Amplify auto-generates (legacy default).
         * - integratorId is UUID and not SRS Roof Hub org-as-id path → explicit id = that UUID (legacy).
         * - SRS parent + integrator + organizationId → explicit id = organizationId (Roof Hub).
         */

        if (srsUseOrgAsId) {
          try {
            const existingRes = await API.graphql(
              graphqlOperation(getSharedApp, { id: organizationTrim })
            );
            if (existingRes?.data?.getSharedApp?.id) {
              addSnack({
                variant: "danger",
                message:
                  "A GoodLink already exists with this Organization ID as partner id. Use a different organization.",
              });
              setSpinner(false);
              return;
            }
          } catch (lookupErr) {
            console.log("Organization id uniqueness check failed", lookupErr);
            addSnack({
              variant: "danger",
              message:
                "Could not verify Organization ID uniqueness. Fix the error and retry.",
            });
            setSpinner(false);
            return;
          }
          createInput = { ...createInput, id: organizationTrim };
        } else if (
          integratorTrim &&
          isAcceptableUuidForPartnerId(integratorTrim)
        ) {
          try {
            const existingRes = await API.graphql(
              graphqlOperation(getSharedApp, { id: integratorTrim })
            );
            if (existingRes?.data?.getSharedApp?.id) {
              addSnack({
                variant: "danger",
                message:
                  "A partner already exists with this id (integrator UUID matches an existing SharedApp). Use a different integrator UUID or a non-UUID integrator id to auto-generate a partner id.",
              });
              setSpinner(false);
              return;
            }
          } catch (lookupErr) {
            console.log("Partner id uniqueness check failed", lookupErr);
            addSnack({
              variant: "danger",
              message:
                "Could not verify partner id uniqueness. Fix the error and retry, or use a non-UUID integrator id to auto-generate.",
            });
            setSpinner(false);
            return;
          }
          createInput = { ...createInput, id: integratorTrim };
        }
        const result = await API.graphql(
          graphqlOperation(createSharedApp, { input: createInput })
        );
        const updatedApp = buildApp(formState, true);
        updatedApp.id = result.data.createSharedApp.id;
        const srsFormUrlCreate = buildSrsGoodleapplusFormUrl(formState);
        if (srsFormUrlCreate) {
          updatedApp.formUrl = srsFormUrlCreate;
        } else if (formState.integratorId && formState.integratorId !== "" && formState.calculatorType === 'LA') {
          updatedApp.formUrl = `https://${window.location.host}/${result.data.createSharedApp.integratorId}/${sharedAppUrlPathClient(result.data.createSharedApp)}/la/${result.data.createSharedApp.id}`;
        } else if (formState.integratorId && formState.integratorId !== "" && formState.calculatorType === 'PA') {
          updatedApp.formUrl = `https://${window.location.host}/${result.data.createSharedApp.integratorId}/${sharedAppUrlPathClient(result.data.createSharedApp)}/${result.data.createSharedApp.id}`;
        } else if (formState.bundleClientId && formState.bundleClientId !== "") {
          updatedApp.formUrl = `https://${window.location.host}/glp/${sharedAppUrlPathClient(result.data.createSharedApp)}/${result.data.createSharedApp.bundleClientId.toLowerCase()}/${result.data.createSharedApp.id}`;
        } else {
          updatedApp.formUrl = `https://${window.location.host}/${sharedAppUrlPathClient(result.data.createSharedApp)}/${result.data.createSharedApp.id}`;
        }
        await API.graphql(
          graphqlOperation(updateSharedApp, { input: updatedApp })
        );
         addSnack({ variant: 'success', message: 'Shared App created successfully!' });
         loadSharedApp(updatedApp);
         navigate(`../edit/${updatedApp.id}`, { replace: false });
      }
    } catch (err) {
      console.log("Error saving/creating configuration:", err);
      addSnack({
        variant: "danger",
        message: err?.errors?.[0]?.message || err?.message || "Error saving configuration",
      });
    }
    setSpinner(false);
  }

  const copyToClipBoard = async (copyMe) => {
    try {
      await navigator.clipboard.writeText(copyMe);
    } catch (err) {
      err.log("Failed to copy!");
    }
  };

  return (
    <Authenticator
      formFields={formFields}
      components={components}
      hideSignUp={true}
    >
      {({ signOut }) => (
        <>
          {spinner && <Loading />}
          <NavBar>
            <LumosButton onClick={signOut} style={styles.signOut}>
              Sign out
            </LumosButton>
          </NavBar>
          <div style={styles.container}>
            <Heading mb={3} variant="h3">
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="stretch"
                alignContent="flex-start"
                wrap="nowrap"
                gap="1rem"
              >
                <View>{id === 'new' ? 'New' : '' } GoodLink Details</View>
                <SearchField
                  label="Search"
                  placeholder="Search by partner id (UUID) or client id…"
                  size="small"
                  onSubmit={(e) => fetchSharedApp(e)}
                  onClear={() => navigate(`../edit/new`, { replace: false })}
                />
              </Flex>
            </Heading>
            <TextField
              size="small"
              value={formState.id}
              label="Id"
              style={styles.input}
              isDisabled
            />
            <SelectField
              style={styles.input}
              label="Status"
              value={formState.status}
              onChange={(e) => setInput("status", e.target.value)}
              size="small"
            >
              <option value="">Please select a Status</option>
              <option value="ACTIVE">{Status.ACTIVE}</option>
              <option value="INPROGRESS">{Status.INPROGRESS}</option>
              <option value="NOTACTIVE">{Status.NOTACTIVE}</option>
              <option value="PENDING">{Status.PENDING}</option>
              <option value="BLOCKED">{Status.BLOCKED}</option>
              <option value="SUSPENDED">{Status.SUSPENDED}</option>
              <option value="TEST">{Status.TEST}</option>
            </SelectField>
            <TextField
              onChange={(event) => setInput("clientId", event.target.value)}
              size="small"
              value={formState.clientId}
              label="Client Id"
              style={styles.input}
            />
            <TextField
              onChange={(event) => setInput("urlSlug", event.target.value)}
              descriptiveText="Optional DBA for the QuickLink URL (/{urlSlug}/{id}). Leave blank to use Client Id."
              size="small"
              value={formState.urlSlug}
              label="DBA / URL slug"
              style={styles.input}
              placeholder="e.g. coolroofs"
            />
            <TextField
              onChange={(event) => setInput("partnerName", event.target.value)}
              size="small"
              value={formState.partnerName}
              label="Partner Name"
              style={styles.input}
            />
            <TextField
              onChange={(event) => setInput("channel", event.target.value)}
              size="small"
              style={styles.input}
              value={formState.channel}
              label="Offer channel"
            />            
            <LumosCard header={{ title: "Selected Offers" }} sx={{ marginBottom: 20 }}>              
              {catalogOffersStatus === "loading" && (
                <Box
                  sx={{ mt: 2, width: "100%" }}
                  role="progressbar"
                  aria-busy="true"
                  aria-label="Loading offer catalog from organization"
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "5px",
                      backgroundColor: "#e8eaed",
                      borderRadius: "999px",
                      overflow: "hidden"
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: "38%",
                        backgroundColor: "#22863a",
                        borderRadius: "999px",
                        "@keyframes editCatalogBarIndeterminate": {
                          "0%": { transform: "translateX(-100%)" },
                          "100%": { transform: "translateX(320%)" }
                        },
                        animation:
                          "editCatalogBarIndeterminate 1.15s ease-in-out infinite"
                      }}
                    />
                  </Box>
                </Box>
              )}
              {catalogOffersStatus === "error" && (
                <Text marginTop="0.5rem" fontSize="0.875rem" color="#b00020">
                  Could not load offer catalog. Check channel, API keys, or connected org settings.
                </Text>
              )}
              {catalogOffersStatus === "no_keys" && formState.id !== "" && (
                <Text marginTop="0.5rem" fontSize="0.875rem" color="#666">
                  API keys are not available for this client; offer names cannot be resolved.
                </Text>
              )}
              {formState.offer?.trim() ? (
                <Box sx={{ mt: 2 }}>                  
                  <Box
                    as="ul"
                    sx={{
                      marginTop: "0.5rem",
                      marginBottom: 0,
                      paddingLeft: "1.25rem",
                      listStyleType: "disc"
                    }}
                  >
                    {formState.offer
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((oid) => {
                        const row = catalogById.get(oid);
                        const active = !!row;
                        const catalogLoading =
                          catalogOffersStatus === "loading";
                        const namePart = active
                          ? catalogOfferDisplayName(row)
                          : "—";
                        let statusText;
                        let statusColor;
                        if (active) {
                          statusText = "active";
                          statusColor = "#22863a";
                        } else if (catalogLoading) {
                          statusText = "searching for active offer";
                          statusColor = "#ffc107";
                        } else {
                          statusText = "non-active";
                          statusColor = "#b00020";
                        }
                        return (
                          <Box
                            as="li"
                            key={oid}
                            sx={{
                              fontSize: "0.875rem",
                              lineHeight: 1.5,
                              marginBottom: "0.35rem"
                            }}
                          >
                            <Text as="span">{oid}</Text>
                            <Text as="span"> - </Text>
                            <Text as="span">{namePart}</Text>
                            <Text as="span"> - </Text>
                            <Text
                              as="span"
                              fontWeight="600"
                              color={statusColor}
                            >
                              {statusText}
                            </Text>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              ) : (
                <Text marginTop="0.5rem" fontSize="0.875rem" color="#666">
                  No offers configured.
                </Text>
              )}
            </LumosCard>
            <SelectField
              style={styles.input}
              label="Category"
              value={formState.category}
              onChange={(e) => setInput("category", e.target.value)}
              size="small"
            >
              <option value="">Please select a Category</option>
              <option value={Categories.HOME_IMPROVEMENT.cat}>{Categories.HOME_IMPROVEMENT.cat}</option>
              <option value={Categories.ROOF.cat}>{Categories.ROOF.cat}</option>
              <option value={Categories.SOLAR.cat}>{Categories.SOLAR.cat}</option>
              <option value={Categories.BATTERY.cat}>{Categories.BATTERY.cat}</option>
            </SelectField>
            <TextField
             onChange={(event) => setInput(`${formState.calculatorType === "PA" ? "paymentAmount" : "loanAmount"}`, event.target.value)}
              size="small"
              style={styles.input}
              descriptiveText="Two point precision decimal."
              value={formState.calculatorType === "PA" ? formState.paymentAmount : formState.loanAmount}
              label={`Average ${formState.calculatorType === "PA" ? "Payment" : "Loan"}  Amount`}
            />
            <TextField
              label="Submitting User Email"
              onChange={(event) =>
                setInput("submittingUserEmail", event.target.value)
              }
              size="small"
              style={styles.input}
              value={formState.submittingUserEmail}
            />
            <TextField
              label="Submitting User First Name"
              onChange={(event) =>
                setInput("submittingUserFname", event.target.value)
              }
              size="small"
              style={styles.input}
              value={formState.submittingUserFname}
            />
            <TextField
              label="Submitting User Last Name"
              onChange={(event) =>
                setInput("submittingUserLname", event.target.value)
              }
              size="small"
              style={styles.input}
              value={formState.submittingUserLname}
            />
            <TextField
              label="Submitting User Phone"
              onChange={(event) =>
                setInput("submittingUserPhone", event.target.value)
              }
              size="small"
              descriptiveText="10 digits, no other characters"
              style={styles.input}
              value={formState.submittingUserPhone}
            />
            <SelectField
              style={styles.input}
              label="Reference logic"
              value={formState.referenceId}
              onChange={(e) => setInput("referenceId", e.target.value)}
              size="small"
            >
              <option value="quickLink">QuickLink</option>
              <option value="mobilePhone">Mobile Phone</option>
              <option value="lastName">Last Name</option>
              <option value="integratorId">Integrator Id</option>
              <option value="goodleapplus">GoodLeapPlus</option>
            </SelectField>
            <SwitchField
              isDisabled={false}
              style={styles.switch}
              label="Send Email"
              labelPosition="end"
              size="default"
              isChecked={formState.sendEmail}
              onChange={(event) => setInput("sendEmail", event.target.checked)}
            />
            <SwitchField
              isDisabled={false}
              style={styles.switch}
              label="Display Form"
              labelPosition="end"
              size="default"
              isChecked={formState.displayIframe}
              onChange={(event) =>
                setInput("displayIframe", event.target.checked)
              }
            />
            <TextField
              label="Form URL"
              onChange={(event) => setInput("formUrl", event.target.value)}
              size="small"
              style={styles.input}
              value={formState.formUrl}
              placeholder={placeholderUrl}
              innerEndComponent={
                <FieldGroupIconButton
                  ariaLabel="Copy"
                  variation="link"
                  onClick={() => copyToClipBoard(formState.formUrl)}
                  height="70%"
                >
                  <i className="gg-clipboard"></i>
                </FieldGroupIconButton>
              }
            />
            <SwitchField
              isDisabled={false}
              style={styles.switch}
              label="Use Connected Orgs"
              labelPosition="end"
              size="default"
              isChecked={formState.useConnectedOrgs}
              onChange={(event) => setInput("useConnectedOrgs", event.target.checked)}
            />
            {formState.apiKeys && formState.clientId !== "" && (
              <Alert
                variation="success"
                isDismissible={false}
                hasIcon={true}
                heading="API Keys in SSM or Connected Orgs token is valid"
              ></Alert>
            )}
            {!formState.apiKeys && formState.id !== "" && (
              <Alert
                variation="warning"
                isDismissible={false}
                hasIcon={true}
                heading="API Keys NOT in SSM or Connected Orgs token is invalid"
              ></Alert>
            )}
            {!formState.sendEmail && !formState.displayIframe && (
              <Alert
                variation="error"
                isDismissible={false}
                hasIcon={true}
                style={styles.space}
                heading="sendEmail and displayIframe actions can't be both false"
              ></Alert>
            )}
                         <Collapsible title="Special Features">
            <LumosCard
                header={{
                  title: "Features for Corporate/Integrator accounts",
                }}
                sx={{ marginTop: 20, marginBottom: 20 }}
              >
            <SelectField
              style={styles.input}
              label="Calculator Type"
              value={formState.calculatorType}
              onChange={(e) => setInput("calculatorType", e.target.value)}
              size="small"
              width="25%"
            >
              <option value=""></option>
              <option value="LA">Loan Amount</option>
              <option value="PA">Payment Amount</option>
            </SelectField>
            <TextField
              onChange={(event) => setInput("integratorId", event.target.value)}
              size="small"
              value={formState.integratorId}
              label="Integrator Id"
              style={styles.input}
              // descriptiveText={"Important! Currently this only works for Daikin where calculator leads with payment amount."}
            />
            <TextField
              onChange={(event) => setInput("redirectUrl", event.target.value)}
              size="small"
              value={formState.redirectUrl}
              label="Redirect Url"
              style={styles.input}
              descriptiveText={"Important! This will only work if client is configured for this feature."}
            />
            <TextField
              onChange={(event) => setInput("bundleClientId", event.target.value)}
              size="small"
              value={formState.bundleClientId}
              label="GoodLeap Financing Plus Bundle ClientId"
              style={styles.input}
            />
            </LumosCard>
            </Collapsible>
            <Divider
              orientation="horizontal"
              style={styles.divider}
              size="small"
            />
            <Button
              style={
                !formState.sendEmail && !formState.displayIframe
                  ? styles.buttonDisabled
                  : styles.button
              }
              isFullWidth={false}
              isDisabled={!formState.sendEmail && !formState.displayIframe}
              onClick={addSharedApp}
            >
              Save partner config
            </Button>
            <Divider
              orientation="horizontal"
              style={styles.divider}
              size="small"
            />
            <Box
              sx={{
                marginBottom: "100px",
              }}
            >
              &nbsp;
            </Box>
          </div>
        </>
      )}
    </Authenticator>
  );
};

const styles = {
  container: {
    width: 960,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    backgroundColor: "#ffffff",
    padding: "20px 20px"
  },
  divider: { margin: "25px 0", color: "white" },
  space: { marginTop: "20px" },
  app: { marginBottom: 15 },
  input: {
    marginBottom: 20,
    padding: 8,
    fontSize: 16,
  },
  switch: {
    marginBottom: 10,
    fontSize: 16,
  },
  appClientId: { fontSize: 20, fontWeight: "bold" },
  appFields: { marginBottom: 0 },
  button: {
    backgroundColor: "#003439",
    color: "white",
    outline: "none",
    fontSize: 18,
    padding: "12px 0px",
    cursor: "pointer",
  },
  buttonDisabled: {
    opacity: 0.8,
    backgroundColor: "#007680",
    color: "white",
    outline: "none",
    fontSize: 18,
    padding: "12px 0px",
    cursor: "notAllowed",
  },
  signOut: {
    marginRight: 10,
  },
  logo: {
    width: 400,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: 20,
  },
  heading: {
    backgroundColor: "#003439",
  },
  clipboardButton: {
    cursor: "pointer",
  },
};

export default Edit;
